import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatomoTrackerService } from 'analytics';
import { I18nService, TranslatePipe } from 'i18n';

import { FunnelService, QuizQuestion, QuizSession } from '../../../services/funnel.service';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

const MATOMO_CATEGORY = 'FAC3LESS Funnel';

/** Where a session payload came from — decides history handling on render. */
type SessionSource = 'fetch' | 'answer' | 'rewind';

/**
 * The quiz wizard. Renders one question at a time driven entirely by the
 * server's `next_question` payload — the frontend doesn't know the branching
 * logic. When the session transitions to `completed`, we navigate to /quiz/result.
 *
 * Even without a session token in storage we attempt to load the session —
 * the backend can resume via the `cid` auth cookie and re-issues a token.
 * Only when that fails (401/404) do we redirect back to /r to restart.
 *
 * The shown question is mirrored into the URL as `?step=N`, so the browser
 * back button (and the in-quiz back link) rewinds the quiz: the server drops
 * the answers from that step onwards and re-asks the question.
 */
@Component({
  selector: 'app-funnel-quiz-page',
  standalone: true,
  imports: [CommonModule, TranslatePipe, FunnelShellComponent],
  templateUrl: './funnel-quiz-page.component.html',
  styleUrls: ['./funnel-quiz-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelQuizPageComponent {
  private router     = inject(Router);
  private route      = inject(ActivatedRoute);
  private funnel     = inject(FunnelService);
  private i18n       = inject(I18nService);
  private matomo     = inject(MatomoTrackerService);
  private destroyRef = inject(DestroyRef);

  loading  = signal(true);
  error    = signal<string | null>(null);
  question = signal<QuizQuestion | null>(null);
  busy     = signal(false);

  /** Step (1-based) of the question currently shown; null until first load. */
  private currentStep: number | null = null;

  constructor() {
    this.fetchSession();

    // Browser back/forward: react to ?step changes. Back to a lower step
    // rewinds the quiz on the server; forward to an undone step is a no-op
    // (the answers are gone), so we just re-pin the URL.
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const urlStep = Number(params.get('step'));
        if (!Number.isFinite(urlStep) || urlStep < 1) return;
        if (this.currentStep === null || this.busy()) return;
        if (urlStep < this.currentStep) {
          this.rewindTo(urlStep);
        } else if (urlStep > this.currentStep) {
          this.syncUrl(this.currentStep, true);
        }
      });
  }

  selectAnswer(value: string): void {
    const q = this.question();
    if (!q || this.busy()) return;

    this.busy.set(true);
    this.error.set(null);

    this.funnel.answerQuiz(q.key, value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: session => {
          this.busy.set(false);
          this.matomo.trackEvent({
            category: MATOMO_CATEGORY,
            action:   'quiz_answered',
            name:     `${q.key}=${value}`
          });
          this.handleSession(session, 'answer');
        },
        error: err => {
          this.busy.set(false);
          this.error.set(err?.error?.error || 'Could not save your answer. Please try again.');
        }
      });
  }

  /** In-quiz back link — same server rewind as the browser back button. */
  goBack(): void {
    const step = this.currentStep;
    if (!step || step <= 1 || this.busy()) return;
    this.rewindTo(step - 1);
  }

  private fetchSession(): void {
    this.funnel.showQuiz()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: session => {
          this.loading.set(false);
          this.handleSession(session, 'fetch');
        },
        error: err => {
          this.loading.set(false);
          // 401 = expired/invalid session — start over.
          if (err?.status === 401 || err?.status === 404) {
            this.funnel.clearSessionToken();
            this.redirectToLanding();
            return;
          }
          this.error.set(err?.error?.error || 'Could not load the quiz.');
        }
      });
  }

  private rewindTo(step: number): void {
    this.busy.set(true);
    this.error.set(null);

    this.funnel.rewindQuiz(step)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: session => {
          this.busy.set(false);
          this.matomo.trackEvent({
            category: MATOMO_CATEGORY,
            action:   'quiz_rewound',
            name:     `to_step_${step}`
          });
          this.handleSession(session, 'rewind');
        },
        error: err => {
          this.busy.set(false);
          if (err?.status === 401 || err?.status === 404) {
            this.funnel.clearSessionToken();
            this.redirectToLanding();
            return;
          }
          if (err?.status === 409) {
            // Purchased — nothing can change anymore, show the result.
            const lang = this.i18n.getLanguage() || 'en';
            this.router.navigate(['/', lang, 'quiz', 'result'], { replaceUrl: true });
            return;
          }
          this.error.set(err?.error?.error || 'Could not go back. Please try again.');
        }
      });
  }

  private handleSession(session: QuizSession, source: SessionSource): void {
    // The backend re-issues a session token when it resumed via the auth cookie.
    if (session.session_token) this.funnel.storeSessionToken(session.session_token);

    if (session.status === 'completed' || session.status === 'purchased') {
      // Browser back from the result page lands here with a completed session
      // and ?step=N in the URL — rewind to that question instead of bouncing
      // straight back to the result.
      const urlStep = Number(this.route.snapshot.queryParamMap.get('step'));
      if (source === 'fetch' && session.status === 'completed' && Number.isFinite(urlStep) && urlStep >= 1) {
        this.rewindTo(urlStep);
        return;
      }

      if (session.recommended_product_code) {
        this.matomo.trackEvent({
          category: MATOMO_CATEGORY,
          action:   'quiz_completed',
          name:     session.recommended_product_code
        });
      }
      const lang = this.i18n.getLanguage() || 'en';
      // Push (don't replace) when the user just answered the final question,
      // so back from the result returns into the quiz at the last step.
      this.router.navigate(['/', lang, 'quiz', 'result'], { replaceUrl: source !== 'answer' });
      return;
    }

    this.question.set(session.next_question);
    const step = session.next_question?.progress.current ?? 1;
    this.currentStep = step;
    // Answers push a history entry (so back walks the questions); initial
    // load and rewinds replace the current one.
    this.syncUrl(step, source !== 'answer');
  }

  private syncUrl(step: number, replace: boolean): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step },
      replaceUrl: replace
    });
  }

  private redirectToLanding(): void {
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate(['/', lang, 'r'], { replaceUrl: true });
  }
}
